import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Building2, CreditCard, Boxes, Briefcase, Activity, Nfc, Users, ReceiptText, Crown, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageLoading, EmptyState, fmtDate } from "@/components/shared";
import api from "@/lib/api";
import { INDUSTRY_LABEL } from "@/lib/industries";

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between border-b border-slate-50 py-2 last:border-0">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-sm font-semibold text-navy">{value ?? "—"}</span>
  </div>
);
const Stat = ({ label, value }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
    <div className="text-2xl font-extrabold text-navy">{value ?? 0}</div>
    <div className="text-xs text-slate-500">{label}</div>
  </div>
);
const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-slate-200 bg-white p-5 ${className}`}>{children}</div>
);

export default function AdminCompanyDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("overview");

  const load = () => api.get(`/admin/companies/${id}/detail`).then((r) => setD(r.data)).catch(() => { toast.error("Error"); nav("/admin/companies"); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const setIndustry = async (ind) => {
    try { await api.post(`/admin/companies/${id}/industry`, { industry: ind }); toast.success(t("f.save")); load(); }
    catch { toast.error("Error"); }
  };

  if (!d) return <PageLoading />;
  const o = d.overview, s = d.subscription;
  const catLabel = (c) => t(`wc.${c}`, c);

  const TABS = [
    { v: "overview", icon: Building2, label: t("cd.tabOverview") },
    { v: "subscription", icon: CreditCard, label: t("cd.tabSubscription") },
    { v: "modules", icon: Boxes, label: t("cd.tabModules") },
    { v: "branche", icon: Briefcase, label: t("cd.tabBranche") },
    { v: "mitarbeiter", icon: Users, label: t("cd.tabEmployees") },
    { v: "nutzung", icon: Activity, label: t("cd.tabUsage") },
    { v: "nfc", icon: Nfc, label: t("cd.tabNfc") },
    { v: "billing", icon: ReceiptText, label: t("cd.tabBilling") },
  ];

  return (
    <div data-testid="admin-company-detail">
      <button onClick={() => nav("/admin/companies")} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy" data-testid="cd-back"><ArrowLeft className="h-4 w-4" /> {t("cd.back")}</button>
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-2xl font-extrabold text-navy sm:text-3xl">{o.name}</h1>
        {o.is_comp && <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700"><Crown className="h-3 w-3" /> {t("adm.coCompBadge")}</span>}
        {o.is_test && <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-600">{t("adm.testBadge")}</span>}
        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${o.status === "ACTIVE" ? "border-emerald-200 bg-emerald-50 text-emerald-600" : "border-red-200 bg-red-50 text-red-600"}`}>{o.status}</span>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 flex h-auto flex-wrap justify-start gap-1 bg-slate-100 p-1">
          {TABS.map((x) => (
            <TabsTrigger key={x.v} value={x.v} data-testid={`cd-tab-${x.v}`} className="gap-1.5 data-[state=active]:bg-white">
              <x.icon className="h-3.5 w-3.5" /> {x.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Übersicht */}
        <TabsContent value="overview">
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label={t("adm.coUsers")} value={o.user_count} />
            <Stat label={t("adm.coEmployees")} value={o.employee_count} />
            <Stat label={t("cd.activePortals")} value={o.active_portals} />
            <Stat label={t("cd.documents")} value={o.document_count} />
          </div>
          <Card>
            <Row label={t("adm.coOwner")} value={<span>{o.owner_name}<span className="ml-1 font-normal text-slate-400">{o.owner_email}</span></span>} />
            <Row label={t("adm.coIndustry")} value={o.industry_label} />
            <Row label={t("cd.country")} value={o.country} />
            <Row label={t("adm.coSize")} value={o.company_size} />
            <Row label={t("adm.coCreated")} value={fmtDate(o.created_at)} />
          </Card>
        </TabsContent>

        {/* Subscription */}
        <TabsContent value="subscription">
          <Card>
            <Row label={t("cd.plan")} value={<span className="uppercase">{s.tier || "—"}</span>} />
            <Row label={t("adm.status")} value={s.status} />
            <Row label={t("cd.active")} value={s.active ? t("cd.yes") : t("cd.no")} />
            <Row label={t("cd.trialEnds")} value={s.trial_ends_at ? fmtDate(s.trial_ends_at) : "—"} />
            <Row label={t("cd.periodEnd")} value={s.current_period_end ? fmtDate(s.current_period_end) : "—"} />
            <Row label={t("cd.interval")} value={s.interval} />
            <Row label={t("cd.canBuyModules")} value={s.can_purchase_premium_modules ? t("cd.yes") : t("cd.no")} />
            <Row label={t("cd.comp")} value={s.comp ? t("cd.yes") : t("cd.no")} />
            <Row label="Stripe Sub" value={s.stripe_subscription_id || "—"} />
          </Card>
        </TabsContent>

        {/* Module / Entitlements */}
        <TabsContent value="modules">
          <div className="grid gap-3 sm:grid-cols-2">
            {d.modules.map((m) => (
              <Card key={m.key} className="flex flex-col gap-2" >
                <div className="flex items-center justify-between">
                  <div className="font-heading font-bold text-navy">{m.name}</div>
                  {m.active ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-600"><Check className="h-3 w-3" /> {t("cd.mActive")}</span>
                    : m.coming_soon ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">{t("cd.mSoon")}</span>
                    : <span className="rounded-full border border-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-400">{t("cd.mInactive")}</span>}
                </div>
                <p className="text-xs text-slate-500">{m.description}</p>
                <div className="flex flex-wrap gap-1.5">
                  {m.recommended && <span className="rounded-full bg-cyan-50 px-2 py-0.5 text-[10px] font-semibold text-cyan-700">{t("cd.mRecommended")}</span>}
                  {m.sellable && <span className="rounded-full bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-500">{t("cd.mSellable")}</span>}
                  {m.entitlement_status && <span className="rounded-full bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-500">{m.entitlement_status}</span>}
                  {m.entitlement_source && <span className="rounded-full bg-cyan-50 px-2 py-0.5 text-[10px] font-semibold text-cyan-700" data-testid={`cd-src-${m.key}`}>{m.entitlement_source === "PFLEGE_COMPLETE_BUNDLE" ? "Pflege Komplett" : m.entitlement_source === "DIRECT_MODULE" ? "Einzelbuchung" : m.entitlement_source}</span>}
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Branche */}
        <TabsContent value="branche">
          <Card className="max-w-md">
            <div className="mb-1 text-sm font-semibold text-navy">{t("adm.coIndustry")}</div>
            <p className="mb-4 text-xs text-slate-500">{t("cd.brancheHint")}</p>
            <Select value={d.branche.industry} onValueChange={setIndustry}>
              <SelectTrigger data-testid="cd-industry-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {d.branche.industries.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {d.branche.recommendations?.length > 0 && (
              <div className="mt-4">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">{t("cd.mRecommended")}</div>
                <div className="flex flex-wrap gap-1.5">
                  {d.branche.recommendations.map((mk) => {
                    const m = d.modules.find((x) => x.key === mk);
                    return <span key={mk} className="rounded-full bg-cyan-50 px-2 py-0.5 text-xs font-semibold text-cyan-700">{m?.name || mk}</span>;
                  })}
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Mitarbeiter */}
        <TabsContent value="mitarbeiter">
          {d.worker_distribution && (
            <div className="mb-4 flex flex-wrap gap-2" data-testid="cd-worker-distribution">
              {Object.entries(d.worker_distribution.by_category).filter(([, n]) => n > 0).map(([k, n]) => (
                <span key={k} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">{t(`wc.${k}`)}: {n}</span>
              ))}
              {d.worker_distribution.by_context?.POSTED_WORKER > 0 && <span className="rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700">{t("p2.ctx.POSTED_WORKER")}: {d.worker_distribution.by_context.POSTED_WORKER}</span>}
              {d.worker_distribution.abroad > 0 && <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">{t("p2.abroadBadge")}: {d.worker_distribution.abroad}</span>}
            </div>
          )}
          {d.employees.length === 0 ? <EmptyState icon={Users} title={t("adm.emEmpty")} /> : (
            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
              <table className="w-full text-sm" data-testid="cd-employees-table">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
                  <tr><th className="px-4 py-3">{t("adm.usName")}</th><th className="px-4 py-3">{t("f.jobTitle")}</th><th className="px-4 py-3">{t("f.nationality")}</th><th className="px-4 py-3">{t("cd.workerCategory")}</th><th className="px-4 py-3">{t("cd.portal")}</th></tr>
                </thead>
                <tbody>
                  {d.employees.map((e) => (
                    <tr key={e.id} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-4 py-3 font-semibold text-navy">{e.first_name} {e.last_name}</td>
                      <td className="px-4 py-3 text-slate-600">{e.job_title || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{e.nationality || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{catLabel(e.worker_category)}</td>
                      <td className="px-4 py-3 text-slate-500">{e.portal_status || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Nutzung */}
        <TabsContent value="nutzung">
          <div className="space-y-5">
            <div>
              <div className="mb-2 font-heading font-bold text-navy">{t("cd.uTime")}</div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label={t("cd.uSites")} value={d.usage.TIME_TRACKING?.sites} />
                <Stat label={t("cd.uActiveTags")} value={d.usage.TIME_TRACKING?.active_tags} />
                <Stat label={t("cd.uClockEvents")} value={d.usage.TIME_TRACKING?.clock_events_total} />
                <Stat label={t("cd.uClockInsToday")} value={d.usage.TIME_TRACKING?.clock_ins_today} />
              </div>
            </div>
            <div>
              <div className="mb-2 font-heading font-bold text-navy">{t("cd.uSubcon")}</div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <Stat label={t("cd.uSubcontractors")} value={d.usage.SUBCONTRACTOR_COMPLIANCE?.subcontractors} />
                <Stat label={t("cd.uSubWorkers")} value={d.usage.SUBCONTRACTOR_COMPLIANCE?.subcontractor_workers} />
                <Stat label={t("cd.uAssignments")} value={d.usage.SUBCONTRACTOR_COMPLIANCE?.active_assignments} />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* NFC / QR */}
        <TabsContent value="nfc">
          {d.nfc.tags.length === 0 ? <EmptyState icon={Nfc} title={t("cd.nfcEmpty")} /> : (
            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
              <table className="w-full text-sm" data-testid="cd-nfc-table">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
                  <tr><th className="px-4 py-3">{t("cd.nfcSite")}</th><th className="px-4 py-3">{t("cd.nfcLabel")}</th><th className="px-4 py-3">{t("adm.status")}</th><th className="px-4 py-3">{t("cd.nfcScans")}</th><th className="px-4 py-3">{t("cd.nfcLastUsed")}</th></tr>
                </thead>
                <tbody>
                  {d.nfc.tags.map((tg) => (
                    <tr key={tg.id} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-4 py-3 font-semibold text-navy">{tg.site_name}</td>
                      <td className="px-4 py-3 text-slate-600">{tg.label || "—"}</td>
                      <td className="px-4 py-3"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${tg.status === "active" ? "border-emerald-200 bg-emerald-50 text-emerald-600" : "border-slate-200 bg-slate-50 text-slate-500"}`}>{tg.status}</span></td>
                      <td className="px-4 py-3 text-slate-600">{tg.scans}</td>
                      <td className="px-4 py-3 text-slate-500">{tg.last_used_at ? fmtDate(tg.last_used_at) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Billing */}
        <TabsContent value="billing">
          <Card className="mb-4 max-w-md">
            <Row label={t("cd.plan")} value={<span className="uppercase">{s.tier || "—"}</span>} />
            <Row label={t("adm.status")} value={s.status} />
            <Row label={t("cd.periodEnd")} value={s.current_period_end ? fmtDate(s.current_period_end) : "—"} />
            <Row label="Stripe Customer" value={s.stripe_customer_id || "—"} />
          </Card>
          <div className="mb-2 font-heading font-bold text-navy">{t("cd.hardwareOrders")}</div>
          {d.billing.hardware_orders.length === 0 ? <EmptyState icon={ReceiptText} title={t("cd.noOrders")} /> : (
            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
              <table className="w-full text-sm" data-testid="cd-billing-table">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
                  <tr><th className="px-4 py-3">{t("adm.status")}</th><th className="px-4 py-3">{t("cd.qty")}</th><th className="px-4 py-3">{t("cd.amount")}</th><th className="px-4 py-3">{t("cd.paidAt")}</th></tr>
                </thead>
                <tbody>
                  {d.billing.hardware_orders.map((ord) => (
                    <tr key={ord.id} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-4 py-3"><span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-600">{ord.status}</span></td>
                      <td className="px-4 py-3 text-slate-600">{ord.quantity || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{ord.amount != null ? `${ord.amount} ${(ord.currency || "").toUpperCase()}` : "—"}</td>
                      <td className="px-4 py-3 text-slate-500">{ord.paid_at ? fmtDate(ord.paid_at) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
